import React, { useEffect, useState } from 'react';
import axiosInstance from '../AxiosInstance';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Button,
  Paper,
  // Modal,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InsightsNotificationBox from '../components/InsightsNotificationBox';
import { trackAppUpdateBannerShown, trackAppUpdateReloaded } from '../services/analytics';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Create striped pattern for projected bars
const createStripedPattern = (color, opacity = 0.3) => {
  const canvas = document.createElement('canvas');
  canvas.width = 10;
  canvas.height = 10;
  const ctx = canvas.getContext('2d');
  
  ctx.fillStyle = `rgba(255, 255, 255, 0)`;
  ctx.fillRect(0, 0, 10, 10);
  
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.globalAlpha = opacity;
  
  ctx.beginPath();
  ctx.moveTo(0, 10);
  ctx.lineTo(10, 0);
  ctx.stroke();
  
  ctx.beginPath();
  ctx.moveTo(-5, 5);
  ctx.lineTo(5, -5);
  ctx.stroke();
  
  ctx.beginPath();
  ctx.moveTo(5, 15);
  ctx.lineTo(15, 5);
  ctx.stroke();
  
  return ctx.createPattern(canvas, 'repeat');
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.common.white,
  fontWeight: 'bold',
}));

const Overview = () => {
  const [overviewData, setOverviewData] = useState([]);
  const [productUnits, setProductUnits] = useState({});
  const [selectedUnits, setSelectedUnits] = useState({});
  const [convertedProfits, setConvertedProfits] = useState({});
  const [convertedProfitsLastWeek, setConvertedProfitsLastWeek] = useState({});
  const [convertedStock, setConvertedStock] = useState({});
  const [productColors, setProductColors] = useState({});
  const [productsBelowThreshold, setProductsBelowThreshold] = useState([]);
  const [productsWithoutInventory, setProductsWithoutInventory] = useState([]);
  const [productsWithMissingSuppliers, setProductsWithMissingSuppliers] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [yesterdaySales, setYesterdaySales] = useState(null);
  // const [modalOpen, setModalOpen] = useState(false);
  
  // Beta feature toggle state
  const [clipBetaEnabled, setClipBetaEnabled] = useState(true);
  const [showVersionBanner, setShowVersionBanner] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState(null);

  // Listen for service worker updates
  useEffect(() => {
    const handleAppUpdateAvailable = (event) => {
      console.log('App update available event received');
      setPendingRegistration(event.detail);
      setShowVersionBanner(true);
      trackAppUpdateBannerShown();
    };

    // Check if there's already a pending registration from index.js
    if (window.pendingServiceWorkerRegistration) {
      setPendingRegistration(window.pendingServiceWorkerRegistration);
      setShowVersionBanner(true);
      trackAppUpdateBannerShown();
    }

    // Listen for future updates
    window.addEventListener('appUpdateAvailable', handleAppUpdateAvailable);
    
    return () => {
      window.removeEventListener('appUpdateAvailable', handleAppUpdateAvailable);
    };
  }, []);

  // Load beta settings from localStorage on component mount
  useEffect(() => {
    try {
      localStorage.setItem('clipBetaEnabled', 'true');
    } catch (e) {}
    setClipBetaEnabled(true);
  }, []);

  useEffect(() => {
    const fetchOverviewData = async () => {
      try {
        const response = await axiosInstance.get('/overview');
        const { finalProfits, weeklySummary: ws, yesterdaySales } = response.data;
        
        if (ws) {
          setWeeklySummary(ws);
        }
        
        if (yesterdaySales !== null && yesterdaySales !== undefined) {
          setYesterdaySales(yesterdaySales);
        }

        const products = finalProfits.profitsForWeek
          .map((item, index) => ({
            productName: item.productName,
            brand: item.brand,
            variety: item.variety,
            size: item.size,
            productId: item.productId,
            profit: item.profit,
            profitLastWeek: finalProfits.profitsForPrevWeek[index]?.profit || 'N/A',
            inventoryUnitId: item.unitId,
            currentStock: item.currentStock,
          }));

        setOverviewData(products);

        const allProducts = products;
        generateUniqueColors(allProducts);

        const unitPromises = products.map((product) =>
          axiosInstance.get(`/units/product/${product.productId}/unitInfo`)
        );

        const unitResponses = await Promise.all(unitPromises);
        const units = unitResponses.reduce((acc, res, idx) => {
          // Backend may return either an array directly or an object with a `units` property
          const unitsArray = Array.isArray(res.data) ? res.data : res.data.units || [];
          acc[products[idx].productId] = unitsArray;
          return acc;
        }, {});

        setProductUnits(units);

        // Set default units for dropdown
        const defaultUnits = products.reduce((acc, product) => {
          acc[product.productId] = product.inventoryUnitId;
          return acc;
        }, {});
        setSelectedUnits(defaultUnits);
      } catch (error) {
        console.error('Error fetching overview data:', error);
      }
    }

    const fetchProductsBelowThreshold = async () => {
      try {
        const response = await axiosInstance.get('/overview/below-threshold');
        setProductsBelowThreshold(response.data.productsBelowThreshold);
      } catch (error) {
        console.error('Error fetching products below threshold:', error);
      }
    };

    const fetchProductsWithoutInventory = async () => {
      try {
        const response = await axiosInstance.get('/overview/without-inventory');
        setProductsWithoutInventory(response.data.productsWithoutInventory);
      } catch (error) {
        console.error('Error fetching products without inventory:', error);
      }
    };

    const fetchProductsWithMissingSuppliers = async () => {
      try {
        const response = await axiosInstance.get('/overview/missing-suppliers');
        setProductsWithMissingSuppliers(response.data.productsWithMissingSuppliers || []);
      } catch (error) {
        console.error('Error fetching products with missing suppliers:', error);
      }
    };

    const fetchForecastData = async () => {
      try {
        const response = await axiosInstance.get('/forecasts/chart-data');
        // Always set forecast data, even when no data is available
        // This ensures charts render with zero values
        setForecastData(response.data);
      } catch (error) {
        console.error('Error fetching forecast data:', error);
        // Set default zero data on error to ensure charts still render
        setForecastData({
          message: 'Error fetching forecast data',
          date: new Date().toISOString().split('T')[0],
          salesChart: {
            lastWeek: 0,
            thisWeek: 0,
            forecast: 0
          },
          profitChart: {
            lastWeek: 0,
            thisWeek: 0,
            forecast: 0
          }
        });
      }
    };

    fetchOverviewData();
    fetchForecastData();
    fetchProductsBelowThreshold();
    fetchProductsWithoutInventory();
    fetchProductsWithMissingSuppliers();
  }, []);

  // Open modal if there are products below threshold OR products without inventory
  // useEffect(() => {
  //   if (productsBelowThreshold.length > 0 || productsWithoutInventory.length > 0) {
  //     setModalOpen(true);
  //   }
  // }, [productsBelowThreshold, productsWithoutInventory]);

  const generateUniqueColors = (products) => {
    const colorMap = {};
    products.forEach((product, index) => {
      if (!colorMap[product.productId]) {
        colorMap[product.productId] = `hsl(${(index * 360) / products.length}, 70%, 50%)`;
      }
    });
    setProductColors(colorMap);
  };

  const handleUnitChange = async (productId, selectedUnitId) => {
    setSelectedUnits((prev) => ({ ...prev, [productId]: selectedUnitId }));
    const product = overviewData.find((item) => item.productId === productId);

    try {
      const response = await axiosInstance.get(
        `/overview/calculate-profit`,
        {
          params: {
            profitPerInventoryUnit: product.profit,
            profitLastWeek: product.profitLastWeek === 'N/A' ? 0 : product.profitLastWeek,
            inventoryUnitId: product.inventoryUnitId,
            selectedUnitId,
            currentStock: product.currentStock,
          },
        }
      );

      setConvertedProfits((prev) => ({
        ...prev,
        [productId]: response.data.profit,
      }));
      setConvertedProfitsLastWeek((prev) => ({
        ...prev,
        [productId]: response.data.profitLastWeek,
      }));

      if (Object.prototype.hasOwnProperty.call(response.data, 'stock')) {
        setConvertedStock((prev) => ({
          ...prev,
          [productId]: response.data.stock,
        }));
      }
    } catch (error) {
      console.error('Error converting profit:', error);
    }
  };

  const truncateLabel = (label) => (label.length > 5 ? `${label.substring(0, 5)}...` : label);

  const formatProfitValue = (value) => {
    if (value === null || value === undefined || value === 'N/A') return 'N/A';
    const num = Number(value);
    if (Number.isNaN(num)) return 'N/A';
    return num.toFixed(2);
  };

  const formatStockValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const num = Number(value);
    if (Number.isNaN(num)) return 'N/A';
    return Number.isInteger(num) ? num : num.toFixed(2);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
  };

  // const handleCloseModal = () => setModalOpen(false);

  const handleReloadApp = () => {
    try {
      console.log('Reloading app with new version');
      
      // If there's a pending service worker registration, activate it first
      if (pendingRegistration && pendingRegistration.waiting) {
        console.log('Activating new service worker');
        // Send message to service worker to skip waiting
        pendingRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
        
        // Listen for the service worker to become active
        pendingRegistration.waiting.addEventListener('controllerchange', () => {
          console.log('Service worker controller changed, reloading page');
          window.location.reload();
        });
        
        // Fallback: reload after a short delay if controllerchange doesn't fire
        setTimeout(() => {
          console.log('Fallback reload');
          window.location.reload();
        }, 1000);
      } else {
        // No pending service worker, just reload normally
        console.log('No pending service worker, reloading normally');
        window.location.reload();
      }
      
      trackAppUpdateReloaded();
      // Hide the banner immediately
      setShowVersionBanner(false);
      
      // Clear the global registration
      window.pendingServiceWorkerRegistration = null;
    } catch (error) {
      console.error('Error reloading app:', error);
      // Fallback to normal reload
      window.location.reload();
    }
  };

  const formatProductDisplay = (product) => {
    let display = product.productName;
    if (product.variety) {
      display += ` - ${product.variety}`;
    }
    if (product.brand) {
      display += ` (${product.brand})`;
    }
    if (product.size) {
      display += ` [${product.size}]`;
    }
    return display;
  };

  const generateAddInventoryLink = (product) => {
    const encodedName = encodeURIComponent(product.productName);
    const encodedVariety = encodeURIComponent(product.variety || '');
    return `/dashboard/inventories/stock-entry?product_id=${product.productId}&product_name=${encodedName}&variety=${encodedVariety}&from_overview=true`;
  };

  // Handle beta toggle change
  const handleClipBetaToggle = (event) => {
    const enabled = event.target.checked;
    setClipBetaEnabled(enabled);
    localStorage.setItem('clipBetaEnabled', JSON.stringify(enabled));
  };

  return (
    <Box data-testid="overview-page" sx={{ padding: 2 }}>
      {showVersionBanner && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="body2">
              A new version of the application is available. Click Reload to update.
            </Typography>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={handleReloadApp}
            >
              Reload
            </Button>
          </Box>
        </Alert>
      )}

      {/* Insights Notification Box */}
      <InsightsNotificationBox />
      
      {/* Products With Missing Suppliers - shown above Products Without Initial Stock Set */}
      {productsWithMissingSuppliers.length > 0 && (
        <Alert sx={{ mb: 2, bgcolor: '#FFF3E0', border: '1px solid', borderColor: '#FFE0B2' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Products With Missing Suppliers
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                The following products have buying units without any suppliers linked. Click a product to add suppliers.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {productsWithMissingSuppliers.map((p) => (
                  <Chip
                    key={`${p.productId}-${p.missingUnitId}`}
                    label={formatProductDisplay(p)}
                    size="small"
                    color="warning"
                    variant="outlined"
                    component={Link}
                    to={`/dashboard/units/edit/${p.missingUnitId}`}
                    clickable
                    sx={{ textDecoration: 'none' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Alert>
      )}
      
      {/* Inventory Notifications - Products Without Initial Stock */}
      {productsWithoutInventory.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Products Without Initial Stock Set
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                The following products do not have their initial stocks set:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {productsWithoutInventory.map((product) => (
                  <Chip
                    key={product.productId}
                    label={formatProductDisplay(product)}
                    size="small"
                    color="error"
                    variant="outlined"
                    component={Link}
                    to={generateAddInventoryLink(product)}
                    clickable
                    sx={{ textDecoration: 'none' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Alert>
      )}

      {/* Inventory Notifications - Low Stock Warning */}
      {productsBelowThreshold.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Low Stock Warning
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                The following products are running low on stock - Click to Generate Reorder Text:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {productsBelowThreshold.map((product) => (
                  <Chip
                    key={product.productId}
                    label={formatProductDisplay(product)}
                    size="small"
                    color="warning"
                    variant="outlined"
                    component={Link}
                    to="/dashboard/inventories/reorder"
                    clickable
                    sx={{ textDecoration: 'none' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Alert>
      )}
      
      {/* Beta Features Toggle (hidden from user as per requirements) */}
      {false && (
        <Card sx={{ mb: 3, bgcolor: clipBetaEnabled ? 'blue.50' : 'grey.50', border: '1px solid', borderColor: clipBetaEnabled ? 'blue.200' : 'grey.300' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  🧪 Beta Features
                  <Chip label="Experimental" size="small" color="warning" />
                </Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={clipBetaEnabled}
                    onChange={handleClipBetaToggle}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      🎯 AI Product Suggestions (CLIP)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {clipBetaEnabled ? 'AI suggestions active - slower processing' : 'Fast mode - no AI processing'}
                    </Typography>
                  </Box>
                }
              />
            </Box>
            
            <Alert severity={clipBetaEnabled ? "info" : "success"} sx={{ mt: 2 }}>
              <Typography variant="body2">
                {clipBetaEnabled ? (
                  <>
                    <strong>🎯 AI Product Suggestions Enabled:</strong> When adding photos in "Add Product", 
                    the system will analyze images and suggest similar products. 
                    <strong> Processing takes 10-30 seconds</strong> per photo.
                  </>
                ) : (
                  <>
                    <strong>⚡ Fast Mode Active:</strong> Product creation is optimized for speed. 
                    Photos can still be taken and saved, but no AI processing delays occur.
                  </>
                )}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      
      {/* Modal for Products Below Threshold and Without Inventory */}
      {/* <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box sx={{ 
          padding: 4, 
          background: 'white', 
          margin: 'auto', 
          marginTop: '5%', 
          width: '60%', 
          maxWidth: '800px',
          borderRadius: 2,
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <Typography variant="h6" gutterBottom>
            Inventory Notifications
          </Typography>
          
          Products Without Inventory Section
          {productsWithoutInventory.length > 0 && (
            <>
              <Typography variant="h6" color="error" gutterBottom>
                Products Without Initial Stock Set
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                The following products do not have their initial stocks set:
              </Typography>
              <TableContainer component={Paper} sx={{ marginBottom: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Variety</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productsWithoutInventory.map((product) => (
                      <TableRow key={product.productId}>
                        <TableCell>{formatProductDisplay(product)}</TableCell>
                        <TableCell>{product.variety || 'N/A'}</TableCell>
                        <TableCell>
                          <Button
                            component={Link}
                            to={generateAddInventoryLink(product)}
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={handleCloseModal}
                          >
                            Set Stock
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          Divider if both sections exist
          {productsWithoutInventory.length > 0 && productsBelowThreshold.length > 0 && (
            <Divider sx={{ marginY: 2 }} />
          )}

          Products Below Threshold Section
          {productsBelowThreshold.length > 0 && (
            <>
              <Typography variant="h6" color="warning.main" gutterBottom>
           Low Stock Warning
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Variety</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productsBelowThreshold.map((product) => (
                      <TableRow key={product.productId}>
                        <TableCell>
                          {product.productName}
                          {product.brand && ` (${product.brand})`}
                        </TableCell>
                        <TableCell>{product.variety || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          Show message if no issues
          {productsWithoutInventory.length === 0 && productsBelowThreshold.length === 0 && (
            <Typography>No inventory issues found.</Typography>
          )}

          <Button variant="contained" onClick={handleCloseModal} sx={{ marginTop: 2 }}>
            Close
          </Button>
        </Box>
      </Modal> */}

      {/* Buttons */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: 3 }}>
        <Link to="/dashboard/purchases/add" style={{ textDecoration: 'none' }}>
          <Button data-testid="add-purchase-btn" variant="contained" color="primary">
            Add Purchase
          </Button>
        </Link>
        <Link to="/dashboard/sales/favorites" style={{ textDecoration: 'none' }}>
          <Button data-testid="add-sale-btn" variant="contained" color="secondary">
            Favorite Product Sales
          </Button>
        </Link>
        <Link to="/dashboard/inventories/view" style={{ textDecoration: 'none' }}>
          <Button variant="contained" color="info">
            Reconcile Inventory
          </Button>
        </Link>
        <Link to="/dashboard/customertransaction" style={{ textDecoration: 'none' }}>
          <Button data-testid="transaction-btn" variant="contained" color="secondary">
            Transaction
          </Button>
        </Link>
      </Box>

      {/* Yesterday's Sales */}
      {yesterdaySales !== null && (
        <Box sx={{ marginBottom: 2, textAlign: 'left' }}>
          <Typography 
            variant="h6" 
            component="h2"
            color="primary" 
            sx={{ 
              fontWeight: 'bold',
              marginBottom: 1
            }}
          >
            Yesterday's Sales (K): {yesterdaySales.toFixed(2)}
          </Typography>
        </Box>
      )}

      {/* Weekly Sales & Profits Charts */}
      {forecastData && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 4 }}>
          {/* Weekly Sales Chart */}
          <Paper data-testid="sales-chart" sx={{ flex: 1, minWidth: 300, padding: 2 }}>
            <Typography variant="h6" gutterBottom>
              Weekly Sales
            </Typography>
            <Box sx={{ height: 200 }}>
              <Bar
                data={{
                  labels: ['Sales Last Week', 'Sales This Week'],
                  datasets: [
                    {
                      label: 'Actual Sales',
                      data: [
                        (forecastData?.salesChart?.lastWeek || 0),
                        (forecastData?.salesChart?.thisWeek || 0),
                      ],
                      backgroundColor: ['#90caf9', '#1976d2'],
                      borderColor: ['#1565c0', '#0d47a1'],
                      borderWidth: 2,
                      barPercentage: 0.8,
                      categoryPercentage: 0.9,
                      minBarLength: 2, // Show vertical line at x=0 for zero values
                    },
                    {
                      label: 'Projected Sales',
                      data: [
                        null, // No projection for last week
                        ((forecastData?.salesChart?.forecast || 0) - (forecastData?.salesChart?.thisWeek || 0)), // Only the additional projected amount
                      ],
                      backgroundColor: createStripedPattern('#1976d2', 0.5),
                      borderColor: '#0d47a1',
                      borderWidth: 2,
                      barPercentage: 0.8,
                      categoryPercentage: 0.9,
                    },
                  ],
                }}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { 
                    legend: { display: false },
                  },
                  scales: {
                    x: { 
                      beginAtZero: true,
                      stacked: true,
                      min: 0,
                      max: undefined, // Let chart auto-scale
                      ticks: {
                        callback: function(value) {
                          return Math.round(value);
                        }
                      }
                    },
                    y: {
                      stacked: true,
                    },
                  },
                }}
              />
            </Box>
          </Paper>

          {/* Weekly Profits Chart */}
          <Paper data-testid="profit-chart" sx={{ flex: 1, minWidth: 300, padding: 2 }}>
            <Typography variant="h6" gutterBottom>
              Weekly Profits
            </Typography>
            <Box sx={{ height: 200 }}>
              <Bar
                data={{
                  labels: ['Profits Last Week', 'Profits This Week'],
                  datasets: [
                    {
                      label: 'Actual Profits',
                      data: [
                        (forecastData?.profitChart?.lastWeek || 0),
                        (forecastData?.profitChart?.thisWeek || 0),
                      ],
                      backgroundColor: ['#a5d6a7', '#388e3c'],
                      borderColor: ['#2e7d32', '#1b5e20'],
                      borderWidth: 2,
                      barPercentage: 0.8,
                      categoryPercentage: 0.9,
                      minBarLength: 2, // Show vertical line at x=0 for zero values
                    },
                    {
                      label: 'Projected Profits',
                      data: [
                        null, // No projection for last week
                        ((forecastData?.profitChart?.forecast || 0) - (forecastData?.profitChart?.thisWeek || 0)), // Only the additional projected amount
                      ],
                      backgroundColor: createStripedPattern('#388e3c', 0.5),
                      borderColor: '#1b5e20',
                      borderWidth: 2,
                      barPercentage: 0.8,
                      categoryPercentage: 0.9,
                    },
                  ],
                }}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { 
                    legend: { display: false },
                  },
                  scales: {
                    x: { 
                      beginAtZero: true,
                      stacked: true,
                      min: 0,
                      max: undefined, // Let chart auto-scale
                      ticks: {
                        callback: function(value) {
                          return Math.round(value);
                        }
                      }
                    },
                    y: {
                      stacked: true,
                    },
                  },
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Estimated ~30% margin
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Inventory Summary Table */}
      <Paper sx={{ padding: 3, marginBottom: 4, overflowX: 'auto' }}>
        <Typography variant="h6" gutterBottom>
          Inventory Summary
        </Typography>
        <TableContainer data-testid="inventory-table">
          <Table sx={{ border: '1px solid #ddd', width: '100%' }}>
            <TableHead>
              <TableRow>
                <StyledTableCell>Product</StyledTableCell>
                <StyledTableCell align="center">Stock</StyledTableCell>
                <StyledTableCell align="center">Profit Per Selected Unit</StyledTableCell>
                <StyledTableCell align="center">Select Unit</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overviewData.map((product) => (
                <TableRow key={product.productId}>
                  <TableCell>
                    <Typography variant="body2">
                      {product.productName}
                      {product.brand && ` (${product.brand})`}
                      {product.variety && ` - ${product.variety}`}
                      {product.size && `, ${product.size}`}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {formatStockValue(
                      convertedStock[product.productId] ?? product.currentStock
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {formatProfitValue(
                      convertedProfits[product.productId] ?? product.profit
                    )}
                  </TableCell>
                  <TableCell align="center">
                  {product.inventoryUnitId ? (
                    <Select
                      value={selectedUnits[product.productId] || product.inventoryUnitId}
                      onChange={(e) => handleUnitChange(product.productId, e.target.value)}
                      fullWidth
                    >
                      {productUnits[product.productId]?.map((unit) => (
                        <MenuItem key={unit.unit_id} value={unit.unit_id}>
                          {unit.unit_type} ({unit.unit_category})
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No units
                    </Typography>
                  )}
                </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Overview;
